<template>
  <div>
    <h2 class="sui-header" id="uploadHeader">ファイル送付</h2>
    <div id="upload" class="ui form">
      <!-- ファイル選択 -->
      <div class="field">
        <label class="required">ファイル選択</label>
        <file-dropper id="file-dropper" @on-drop-file="onDrop"></file-dropper>
      </div>

      <!-- 選択ファイルリスト -->
      <table id="Upload-fileTable" class="ui table" v-if="files.length">
        <thead>
          <tr class="single line">
            <th width="30px">No.</th>
            <th>ファイル名</th>
            <th>ファイルサイズ</th>
            <th width="100px">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(file, index) in files" :key="index">
            <td class="Upload-fileTable__no">{{index + 1}}</td>
            <td class="Upload-fileTable__fileName newline-wrap">{{file.name}}</td>
            <td class="Upload-fileTable__fileSize">{{file.size | bytes}}</td>
            <td class="Upload-fileTable__operation">
              <button
                class="ui button orange tiny Upload-fileTable__delete"
                @click="removeSelectedFile(index)"
              >
                削除
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="error" v-if="isFileSizeLimit">アップロードできるファイルサイズは合計1GiBまでです</div>
      <div class="error" v-if="isFileNumLimit && files.length > 0">同時にアップロード可能なファイル数は10ファイルです</div>

      <!-- 件名 -->
      <div class="field" :class="{ error: $v.title.$error }">
        <label class="required">件名</label>
        <input
          id="Upload-titleInput"
          type="text"
          placeholder
          v-model.trim="title"
          @blur="$v.title.$touch()"
          @focus="$v.title.$reset()"
        />
      </div>
      <div class="error" v-if="$v.title.$error && !$v.title.required">
        件名を入力してください
      </div>
      <div class="error" v-if="$v.title.$error && !$v.title.maxLength">
        件名の長さは60文字までです
      </div>

      <!-- ダウンロードパスワード -->
      <div class="field" :class="{ error: $v.password.$error }">
        <label class="required">ダウンロードパスワード</label>
        <div class="ui action input">
          <input
            id="Upload-passwordInput"
            type="text"
            placeholder
            v-model.trim="password"
            @blur="$v.password.$touch()"
            @focus="$v.password.$reset()"
          />
          <button
            id="Upload-createPasswordButton"
            class="ui button blue"
            @click="createPassword"
          >
            自動生成
          </button>
        </div>
      </div>
      <div class="error" v-if="$v.password.$error && !$v.password.required">
        ダウンロードパスワードを入力してください
      </div>

      <!-- 宛先 -->
      <div class="field" :class="{ 'error': $v.addresses.$error }">
        <label class="required">宛先（改行、カンマもしくはセミコロンで複数メールアドレスを指定可）</label>
        <div class="ui input">
          <textarea
            id="Upload-addressesTextarea"
            rows="2"
            v-model.trim="addresses"
            @blur="$v.addresses.$touch()"
            @focus="$v.addresses.$reset()"
          ></textarea>
        </div>
      </div>
      <div class="error" v-if="$v.addresses.$error && !$v.addresses.required">
        宛先を入力してください
      </div>
      <div
        class="error"
        v-if="
          $v.addresses.$error && $v.addresses.required && !$v.addresses.email
        "
      >
        E-mail形式で入力してください
      </div>
      <div
        class="error"
        v-if="
          $v.addresses.$error &&
            $v.addresses.required &&
            !$v.addresses.maxLength
        "
      >
        送信先は10件までです
      </div>

      <!-- コメント -->
      <div class="field" :class="{ 'error': $v.comments.$invalid }">
        <label>コメント（ファイル内容の補足）</label>
        <textarea
          id="Upload-commentsTextarea"
          v-model="comments"
          @blur="$v.comments.$touch()"
          @focus="$v.comments.$reset()"
        ></textarea>
      </div>
      <div class="error" v-if="$v.comments.$invalid && !$v.comments.maxLength">
        コメントの長さは200文字までです
      </div>

      <div class="center-content">
        <button
          id="Upload-uploadButton"
          class="ui button green wide"
          @click="upload"
          :class="{ disabled: $v.$invalid || isUploadLimit }"
        >
          アップロード
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FileDropper from '@/components/FileDropper.vue'
import { uploadExchangeFiles } from '@/api'
import { required, helpers } from 'vuelidate/lib/validators'
import { getRandomStr, ERROR_MESSAGE } from '@/common'
import _ from 'lodash'

/**
 * E-mail形式のパターン
 * 「,」「;」「\n」を区切りに一つのメールアドレス形式に書かれているかを見る
 */
const emailPattern = helpers.regex(
  'email',
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*([,;\n]\s*[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*)*$/
)

/** 合計ファイルサイズ */
const maxFileSize = 1024 * 1024 * 1024

export default {
  title: 'ファイル送付',
  components: {
    FileDropper
  },
  data: function () {
    return {
      files: [],
      title: '[Acroquest]',
      password: '',
      addresses: '',
      comments: ''
    }
  },
  validations: {
    title: {
      required,
      maxLength: value => helpers.len(value) <= 60
    },
    password: {
      required
    },
    addresses: {
      required,
      emailPattern,
      maxLength: value => helpers.len(value.split(/[,;\n]/)) <= 10
    },
    comments: {
      maxLength: value => helpers.len(value) <= 200
    }
  },
  methods: {
    onDrop: function (files, containsDirectory) {
      let errorMessages = []

      let duplicate = false
      for (let file of files) {
        // ファイル名の重複確認
        if (this.files.some(f => f.name === file.name)) {
          duplicate = true
        } else {
          this.files.push(file)
        }
      }

      if (duplicate) {
        errorMessages.push(ERROR_MESSAGE.DUPLICATE_FILENAME)
      }
      if (containsDirectory) {
        errorMessages.push(ERROR_MESSAGE.CONTAINS_DIRECTORY)
      }

      if (errorMessages.length > 0) {
        this.$store.commit('infoModal/openModal', {
          type: 'ERROR',
          title: 'ファイル選択',
          message: errorMessages
        })
      }
    },
    createPassword: function () {
      this.password = getRandomStr(20)
    },
    upload: function () {
      const self = this
      this.$store.commit('confirmModal/openModal', {
        title: 'ファイルアップロード',
        message: 'ファイルをアップロードします。\nよろしいですか？',
        executeLabel: 'アップロード',
        callback: function (answer) {
          if (answer) {
            self.$store.commit('progress/setLoading', true)
            let addresses = self.addresses.replace(/ /g, '')
            addresses = addresses.split(/[,;\n]/)
            uploadExchangeFiles(
              addresses,
              self.password,
              self.title,
              self.files,
              self.comments
            )
              .then(() => {
                self.$router.push({ name: 'exchangeList' })
              })
              .catch(error => {
                console.log(error)
                self.$store.commit('progress/setLoading', false)
                self.$store.commit('infoModal/openModal', {
                  type: 'ERROR',
                  title: 'ファイルアップロード',
                  message:
                    '通信エラーが発生しました。\nしばらくしてからもう一度お試しください。'
                })
              })
          }
        }
      })
    },
    removeSelectedFile: function (index) {
      this.files.splice(index, 1)
    }
  },
  computed: {
    isUploadLimit: function () {
      return this.isFileSizeLimit || this.isFileNumLimit
    },
    isFileSizeLimit: function () {
      const allSize = _.reduce(_.map(this.files, function (file) {
        return file.size
      }), function (sum, size) {
        return sum + size
      }, 0)
      return allSize > maxFileSize
    },
    isFileNumLimit: function () {
      return this.files.length <= 0 || this.files.length > 10
    }
  }
}
</script>

<style>
#upload {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

h2#uploadHeader {
  max-width: 600px;
  margin: auto;
  margin-bottom: 20px;
}

.ui.button.wide {
  width: 400px;
  margin-top: 20px;
  font-size: large;
}
.required:after {
  content: ' *';
  color: red;
}
</style>
